import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import {
  ASSETS, CORE_NODES, ECONOMY_FLOW, HERO_HUD, MISSIONS, PROTOCOL_STEPS, TRADE_LINKS,
} from "@/data/content";
import { fetchMissions } from "@/lib/api";
import Reveal from "@/components/arc/Reveal";
import Marquee from "@/components/arc/Marquee";
import { SectionHeader, GlowButton, StatusBadge, SimTag, Panel } from "@/components/arc/ui";
import { GlowOrbs, Particles } from "@/components/arc/Backdrops";

const mask = (delay) => ({
  initial: { y: "115%" },
  animate: { y: 0 },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
});

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="scanlines relative flex min-h-[100svh] items-center overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY }} aria-hidden="true">
        <img
          src={ASSETS.background}
          alt="ARCIMUS humanoid commander overlooking a futuristic Arc city"
          className="h-[115%] w-full object-cover object-[center_20%]"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-abyss/90 via-abyss/30 to-abyss/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-abyss via-transparent to-abyss/60" />
        <div className="grid-overlay absolute inset-0 opacity-30" />
      </motion.div>

      <motion.aside
        style={{ opacity: fade }}
        className="absolute right-8 top-1/2 hidden -translate-y-1/2 flex-col gap-5 min-[1440px]:flex"
        aria-label="System HUD"
      >
        {HERO_HUD.map((hud, i) => (
          <motion.div
            key={hud.label}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1 + i * 0.12, duration: 0.6 }}
            className="glass-panel clip-angular-sm px-5 py-3 text-right"
          >
            <p className="hud-label">{hud.label}</p>
            <p className="hud-value mt-1 text-electric text-glow-blue">{hud.value}</p>
          </motion.div>
        ))}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }}>
          <SimTag>CONCEPTUAL INTERFACE</SimTag>
        </motion.div>
      </motion.aside>

      <div className="relative mx-auto w-full max-w-[1600px] px-5 pb-24 pt-32 md:px-8">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          className="font-term text-[11px] tracking-[0.5em] text-electric"
          data-testid="hero-eyebrow"
        >
          ARCIMUS OS {"//"} PRIME-001 {"//"} SYSTEM LIVE
        </motion.p>

        <div className="mt-6 overflow-hidden">
          <motion.h1
            {...mask(0.35)}
            data-testid="hero-title"
            className="font-display text-[17vw] font-black leading-[0.95] tracking-[0.04em] text-chrome text-glow-blue sm:text-7xl lg:text-8xl xl:text-[7.5rem]"
          >
            ARCIMUS
          </motion.h1>
        </div>
        <div className="overflow-hidden">
          <motion.p
            {...mask(0.5)}
            className="mt-3 font-term text-xs tracking-[0.55em] text-ice sm:text-sm md:text-base"
            data-testid="hero-tagline"
          >
            THE FIRST PRIME OF ARC
          </motion.p>
        </div>

        <div className="mt-10 space-y-1 font-display text-3xl font-bold tracking-[0.14em] sm:text-4xl lg:text-5xl">
          {["RISE.", "PROTECT.", "TRANSACT."].map((word, i) => (
            <div key={word} className="overflow-hidden">
              <motion.span
                {...mask(0.75 + i * 0.18)}
                className={`block ${i === 2 ? "text-electric text-glow-blue" : "text-chrome/90"}`}
                data-testid={`hero-slogan-${i}`}
              >
                {word}
              </motion.span>
            </div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-8 max-w-xl text-sm leading-relaxed text-silver md:text-base"
          data-testid="hero-subcopy"
        >
          A humanoid intelligence designed for an economy where machines, agents, and humans
          operate on the same on-chain infrastructure.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.7, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <GlowButton to="/terminal" testId="hero-enter-arcimus-btn">ENTER ARCIMUS</GlowButton>
          <GlowButton to="/protocol" variant="ghost" testId="hero-explore-protocol-btn">
            EXPLORE THE PROTOCOL
          </GlowButton>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.9, duration: 0.8 }}
          className="mt-6 flex flex-wrap items-center gap-3"
          data-testid="hero-token-strip"
        >
          <span className="clip-angular-sm border border-epurple/40 bg-abyss/70 px-4 py-2 font-term text-[10px] tracking-[0.28em] text-silver">
            CA <span className="ml-2 text-epurple">COMING SOON</span>
          </span>
          <a
            href={TRADE_LINKS.buy}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-buy-link"
            className="clip-angular-sm border border-ice/25 bg-abyss/70 px-4 py-2 font-term text-[10px] tracking-[0.28em] text-ice transition-colors duration-300 hover:border-electric hover:shadow-[0_0_18px_rgba(0,168,255,0.4)]"
          >
            BUY ↗ ARGUS.WORLD
          </a>
          <a
            href={TRADE_LINKS.chart}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-chart-link"
            className="clip-angular-sm border border-ice/25 bg-abyss/70 px-4 py-2 font-term text-[10px] tracking-[0.28em] text-ice transition-colors duration-300 hover:border-electric hover:shadow-[0_0_18px_rgba(0,168,255,0.4)]"
          >
            CHART ↗ DEXSCREENER
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 0.8 }}
          className="mt-12 grid max-w-xl grid-cols-2 gap-3 sm:grid-cols-3 min-[1440px]:hidden"
        >
          {HERO_HUD.map((hud) => (
            <div key={hud.label} className="glass-panel clip-angular-sm px-4 py-3">
              <p className="hud-label">{hud.label}</p>
              <p className="hud-value mt-1 text-electric">{hud.value}</p>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 md:flex"
        aria-hidden="true"
      >
        <span className="hud-label">SCROLL</span>
        <ChevronDown className="h-4 w-4 animate-bounce text-electric" />
      </motion.div>
    </section>
  );
};

const Awakening = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.18, 1]);
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section ref={ref} data-testid="awakening-section" className="relative overflow-hidden py-24 md:py-36">
      <GlowOrbs />
      <div className="relative mx-auto grid max-w-[1600px] items-center gap-14 px-5 md:px-8 lg:grid-cols-2">
        <div>
          <Reveal>
            <SectionHeader index="01" eyebrow="AWAKENING" title="THE MACHINE AWAKENS" />
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 space-y-6 text-base leading-relaxed text-silver md:text-lg">
              <p>Machines were built to execute commands.</p>
              <p className="text-chrome">ARCIMUS represents a different generation.</p>
              <ul className="space-y-3 border-l-2 border-arc/60 pl-6 font-term text-sm tracking-[0.12em] text-ice md:text-base">
                <li>A machine with identity.</li>
                <li>A machine with a wallet.</li>
                <li>A machine with missions.</li>
                <li>A machine capable of interacting with an on-chain economy.</li>
              </ul>
              <p>The next era is not only about smarter software.</p>
              <p className="font-display text-lg tracking-[0.1em] text-electric text-glow-blue md:text-xl">
                IT IS ABOUT MACHINES BECOMING ECONOMIC PARTICIPANTS.
              </p>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <div className="clip-angular relative overflow-hidden border border-ice/15">
            <motion.img
              src={ASSETS.banner}
              alt="ARCIMUS humanoid reaching forward, Arc city behind"
              style={{ scale, y }}
              className="h-[420px] w-full object-cover object-[center_25%] md:h-[560px]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-abyss/80 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
              <span className="hud-label">UNIT {"//"} ARCIMUS-001</span>
              <StatusBadge status="LIVE" />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const ORBIT_POS = [
  { top: "0%", left: "50%" },
  { top: "50%", left: "100%" },
  { top: "100%", left: "50%" },
  { top: "50%", left: "0%" },
];

const Core = () => {
  const [active, setActive] = useState(CORE_NODES[0]);
  return (
    <section data-testid="core-section" className="scanlines relative overflow-hidden border-y border-ice/10 bg-space/50 py-24 md:py-36">
      <Particles count={18} />
      <div className="relative mx-auto max-w-[1600px] px-5 md:px-8">
        <Reveal>
          <SectionHeader index="02" eyebrow="CORE" title="THE ARCIMUS CORE" align="center" />
        </Reveal>

        <div className="mt-16 hidden items-center justify-center lg:flex">
          <div className="relative aspect-square w-[560px]">
            <div className="absolute inset-16 rounded-full border border-ice/15" />
            <div className="absolute inset-28 rounded-full border border-electric/20" />
            <div className="absolute left-1/2 top-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2">
              <span className="absolute inset-0 animate-pulse-ring rounded-full border border-electric/50" />
              <span className="absolute inset-0 animate-pulse-ring rounded-full border border-arc/50" style={{ animationDelay: "1.4s" }} />
              <img
                src={ASSETS.logo}
                alt="ARCIMUS core emblem"
                className="h-full w-full rounded-full object-cover shadow-[0_0_60px_rgba(22,119,255,0.55)] ring-1 ring-ice/40"
              />
            </div>
            <div className="absolute inset-0 animate-orbit-slow">
              {CORE_NODES.map((node, i) => (
                <div
                  key={node.id}
                  className="absolute"
                  style={{ ...ORBIT_POS[i], transform: "translate(-50%, -50%)" }}
                >
                  <div className="animate-orbit-rev">
                    <button
                      type="button"
                      data-testid={`core-node-${node.id}`}
                      onMouseEnter={() => setActive(node)}
                      onFocus={() => setActive(node)}
                      onClick={() => setActive(node)}
                      className={`clip-angular-sm border px-5 py-3 font-display text-xs font-bold tracking-[0.24em] transition-all duration-300 ${
                        active.id === node.id
                          ? "border-electric bg-arc/25 text-ice shadow-[0_0_30px_rgba(0,168,255,0.5)]"
                          : "border-ice/25 bg-abyss/80 text-silver hover:border-electric/60 hover:text-ice"
                      }`}
                    >
                      {node.title}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mx-auto mt-10 max-w-2xl">
          <Panel className="p-6 md:p-8" data-testid="core-detail-panel">
            <div className="flex items-center justify-between gap-4">
              <h3 className="font-display text-lg font-bold tracking-[0.2em] text-ice" data-testid="core-detail-title">
                {active.title}
              </h3>
              <span className="hud-label hidden sm:block">CORE NODE</span>
            </div>
            <p className="mt-3 font-term text-xs tracking-[0.18em] text-electric">{active.line}</p>
            <p className="mt-4 text-sm leading-relaxed text-silver md:text-base">{active.detail}</p>
          </Panel>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:hidden">
          {CORE_NODES.map((node) => (
            <button
              key={node.id}
              type="button"
              data-testid={`core-node-mobile-${node.id}`}
              onClick={() => setActive(node)}
              className={`clip-angular-sm border p-5 text-left transition-colors duration-300 ${
                active.id === node.id ? "border-electric bg-arc/20" : "border-ice/15 bg-space/60"
              }`}
            >
              <p className="font-display text-sm font-bold tracking-[0.2em] text-ice">{node.title}</p>
              <p className="mt-2 font-term text-[11px] tracking-[0.12em] text-silver">{node.line}</p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

const ProtocolTeaser = () => (
  <section data-testid="protocol-teaser" className="relative py-24 md:py-36">
    <div className="mx-auto max-w-[1600px] px-5 md:px-8">
      <Reveal>
        <SectionHeader index="03" eyebrow="PROTOCOL" title="THE MACHINE PROTOCOL" />
      </Reveal>
      <div className="mt-14 grid gap-px overflow-hidden border border-ice/10 bg-ice/10 sm:grid-cols-2 lg:grid-cols-6">
        {PROTOCOL_STEPS.map((step, i) => (
          <Reveal key={step.id} delay={i * 0.08} y={20}>
            <Link
              to="/protocol"
              data-testid={`protocol-step-${step.key}`}
              className="group flex h-full flex-col bg-abyss p-6 transition-colors duration-300 hover:bg-steel/50"
            >
              <span className="font-term text-[10px] tracking-[0.3em] text-electric">{step.id}</span>
              <span className="mt-4 font-display text-sm font-bold tracking-[0.16em] text-chrome group-hover:text-ice">
                {step.title}
              </span>
              <span className="mt-auto pt-6 font-term text-[10px] tracking-[0.3em] text-silver/60 transition-colors group-hover:text-electric">
                {"↓"}
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <div className="mt-10">
          <GlowButton to="/protocol" variant="ghost" testId="teaser-protocol-btn">
            OPEN THE PROTOCOL
          </GlowButton>
        </div>
      </Reveal>
    </div>
  </section>
);

const EconomyTeaser = () => (
  <section data-testid="economy-teaser" className="relative overflow-hidden border-y border-ice/10 bg-space/50 py-24 md:py-36">
    <GlowOrbs />
    <div className="relative mx-auto max-w-[1600px] px-5 md:px-8">
      <Reveal>
        <SectionHeader index="04" eyebrow="ECONOMY" title="THE MACHINE ECONOMY" />
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-silver md:text-lg">
          The next economy may not be composed only of humans. Machines can consume resources,
          provide services, coordinate, and transact — settled across USDC rails inside the Arc
          ecosystem.
        </p>
      </Reveal>
      <div className="mt-12 flex flex-wrap items-stretch gap-3">
        {ECONOMY_FLOW.map((f, i) => (
          <Reveal key={f.key} delay={i * 0.07} y={16}>
            <div className="flex items-center gap-3">
              <div className="clip-angular-sm glass-panel px-5 py-4">
                <p className="font-display text-xs font-bold tracking-[0.2em] text-ice">{f.label}</p>
              </div>
              {i < ECONOMY_FLOW.length - 1 && (
                <span className="hidden font-term text-electric md:block">→</span>
              )}
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.25}>
        <div className="mt-10">
          <GlowButton to="/economy" variant="ghost" testId="teaser-economy-btn">
            ENTER THE ECONOMY
          </GlowButton>
        </div>
      </Reveal>
    </div>
  </section>
);

const MissionsTeaser = () => {
  const [missions, setMissions] = useState(MISSIONS.slice(0, 3));
  useEffect(() => {
    fetchMissions().then((data) => setMissions(data.slice(0, 3)));
  }, []);
  return (
    <section data-testid="missions-teaser" className="relative py-24 md:py-36">
      <div className="mx-auto max-w-[1600px] px-5 md:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <SectionHeader index="05" eyebrow="MISSIONS" title="MISSION CONTROL" />
          </Reveal>
          <Reveal delay={0.15}>
            <SimTag>SIMULATED MISSIONS</SimTag>
          </Reveal>
        </div>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {missions.map((m, i) => (
            <Reveal key={m.id} delay={i * 0.1}>
              <Link to="/missions" data-testid={`mission-teaser-${m.id}`}>
                <Panel className="group h-full p-6 transition-shadow duration-300 hover:shadow-[0_0_40px_rgba(22,119,255,0.25)]">
                  <div className="flex items-center justify-between">
                    <span className="font-term text-[10px] tracking-[0.3em] text-silver">{m.code}</span>
                    <StatusBadge status={m.status} />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-bold tracking-[0.14em] text-chrome group-hover:text-ice">
                    {m.name}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-silver">{m.objective}</p>
                  <div className="mt-6 flex items-center justify-between border-t border-ice/10 pt-4">
                    <span className="hud-label">DIFFICULTY</span>
                    <span className="font-term text-xs tracking-[0.2em] text-electric">
                      {"▮".repeat(m.difficulty)}{"▯".repeat(5 - m.difficulty)}
                    </span>
                  </div>
                </Panel>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-10">
            <GlowButton to="/missions" variant="ghost" testId="teaser-missions-btn">
              OPEN MISSION CONTROL
            </GlowButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const TerminalTeaser = () => (
  <section data-testid="terminal-teaser" className="relative overflow-hidden border-t border-ice/10 py-24 md:py-36">
    <div className="absolute inset-0" aria-hidden="true">
      <img
        src={ASSETS.background}
        alt=""
        className="h-full w-full object-cover object-[center_80%] opacity-25"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-abyss via-abyss/85 to-abyss" />
    </div>
    <div className="relative mx-auto grid max-w-[1600px] items-center gap-14 px-5 md:px-8 lg:grid-cols-2">
      <Reveal>
        <div>
          <SectionHeader index="06" eyebrow="TERMINAL" title="ENTER THE TERMINAL" />
          <p className="mt-8 max-w-lg text-base leading-relaxed text-silver md:text-lg">
            Speak to the machine directly. The ARCIMUS command terminal is a live interface
            simulation — type commands, query the system, explore the lore.
          </p>
          <div className="mt-10">
            <GlowButton to="/terminal" testId="teaser-terminal-btn">LAUNCH TERMINAL</GlowButton>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.2}>
        <div className="glass-panel clip-angular overflow-hidden">
          <div className="flex items-center justify-between border-b border-ice/10 px-5 py-3">
            <span className="font-term text-[10px] tracking-[0.3em] text-silver">ARCIMUS OS v1.0</span>
            <SimTag />
          </div>
          <div className="p-6 font-term text-xs leading-7 text-ice/90 md:text-sm">
            <p>{">"} INITIALIZING...</p>
            <p>{">"} CORE ........ <span className="text-electric">ONLINE</span></p>
            <p>{">"} IDENTITY .... <span className="text-electric">ONLINE</span></p>
            <p>{">"} WALLET ...... <span className="text-electric">READY</span></p>
            <p>{">"} NETWORK ..... ARC</p>
            <p>{">"} MODE ........ AUTONOMOUS</p>
            <p className="mt-3 text-chrome">
              ARCIMUS{">"} <span className="ml-1 inline-block h-4 w-2 animate-blink bg-electric align-middle" />
            </p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const Home = () => (
  <>
    <Hero />
    <Marquee />
    <Awakening />
    <Core />
    <ProtocolTeaser />
    <EconomyTeaser />
    <MissionsTeaser />
    <TerminalTeaser />
  </>
);

export default Home;
